import React, {
  Dispatch,
  FormEventHandler,
  SetStateAction,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  Control,
  CONTROL_STATUS,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { ModalFormId } from 'src/components/ModalForms'
import { GridColumnFlexEnd } from 'src/components/ModalForms/ModalForm.styles'
import {
  ControlModelId,
  LabelsColumn,
} from 'src/pages/Controls/IndexItem/ControlsListItem.components'
import { DepartmentChip } from 'src/pages/Controls/DepartmentChip'
import { useSearchableControls } from 'src/pages/Controls/ControlsIndexPage/ControlsIndexPage.hooks'
import { useControlStatuses } from 'src/pages/AccountSettings/AccountSettings.hooks'
import { useComplianceFrameworks } from 'src/components/async/complianceframework'
import { useComplianceFrameworkObjectives } from 'src/components/async/Framework/useComplianceFrameworkObjectives'
import { getControlStatusName } from 'src/components/PageLayout/ShowPage/Dropdowns/Dropdowns.constants'
import { Grid, GridColumn, GridRow } from '../Grid'
import { Title } from '../Text/Title'
import { ControlPolicyInfo } from '../ListItem/ListItem.components'
import { CheckBoxInput } from '../Inputs/CheckBox/CheckBoxInput'
import { TextButton } from '../Buttons/TextButton'
import { LocalFilterDropdown } from './LocalFilterDropdown'
import {
  PickerFrame,
  matchSearch,
  useLabelFilterOptions,
  usePickerSelection,
} from './PickerFrame'
import {
  ControlPickerFilters,
  NO_CONTROL_PICKER_FILTERS,
  buildFrameworkFilterOptions,
  buildMoreFilterOptions,
  controlMatchesPickerFilters,
  hasControlPickerFilters,
} from './controlPicker.helpers'

const ROW_COLUMNS =
  'min-content minmax(min-content, auto) minmax(min-content, auto) minmax(min-content, 1fr)'

type ControlPickerRowProps = {
  control: Control
  checked: boolean
  disabled?: boolean
  onToggle: (controlId: string) => void
  renderOptionalColumn?: (control: Control, isSelected: boolean) => JSX.Element
  showLabels: boolean
}

// Memoized, so toggling one control re-renders one row rather than every row in a list
// that can run to thousands. Callers keep renderOptionalColumn stable (useCallback).
const ControlPickerRow = memo(
  ({
    control,
    checked,
    disabled,
    onToggle,
    renderOptionalColumn,
    showLabels,
  }: ControlPickerRowProps): JSX.Element => {
    const controlId = control.getId()
    const controlAsObject = useMemo(() => control.toObject(), [control])
    return (
      <GridRow id={`control-picker-item-${controlId}`}>
        <GridColumn>
          <CheckBoxInput
            id={`control-picker-${controlId}`}
            aria-label={`Select ${control.getName()}`}
            checked={checked}
            onClick={(e) => e.stopPropagation()}
            onChange={() => onToggle(controlId)}
            disabled={disabled}
          />
        </GridColumn>
        <GridColumn>
          <DepartmentChip departmentId={control.getDepartmentId()} />
        </GridColumn>
        <GridColumn>
          <ControlModelId modelId={control.getModelId()} />
        </GridColumn>
        <GridColumn
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="center"
        >
          <ControlPolicyInfo
            control={controlAsObject}
            isControlNa={control.getStatus() === CONTROL_STATUS.NOTAPPLICABLE}
          />
          <Title name={control.getName()} />
        </GridColumn>
        {renderOptionalColumn && (
          <GridColumnFlexEnd>
            {renderOptionalColumn(control, checked)}
          </GridColumnFlexEnd>
        )}
        {showLabels && <LabelsColumn control={controlAsObject} />}
      </GridRow>
    )
  },
)
ControlPickerRow.displayName = 'ControlPickerRow'

type ControlPickerProps = {
  // The controls on offer; undefined while they load.
  controls: Control[] | undefined
  isLoading?: boolean
  selectedIds: string[]
  setSelectedIds: Dispatch<SetStateAction<string[]>>
  formId: ModalFormId
  onSubmit: FormEventHandler<HTMLFormElement>
  // Text shown above the filters.
  title?: string
  disabled?: boolean
  // Filters and search start over when this changes, e.g. each time the modal opens.
  resetKey?: unknown
  // An extra column at the end of each row, e.g. a status pill. Keep it stable
  // (useCallback) so rows stay memoized.
  renderOptionalColumn?: (control: Control, isSelected: boolean) => JSX.Element
  optionalColumnWidth?: string
  // Buttons shown after Select all / Deselect all, e.g. "Select failing".
  extraSelectButtons?: JSX.Element
  hideSelectButtons?: boolean
  emptyState?: JSX.Element
}

/**
 * A list of controls to pick from, with label, framework and "more" filters, a search
 * line and select / deselect all. Filters and search live in local state, not the URL,
 * so the picker can sit in a modal over any page. Select all and Deselect all act on the
 * controls the filters and search leave showing; selections made under other filters
 * are kept.
 */
export const ControlPicker = ({
  controls,
  isLoading,
  selectedIds,
  setSelectedIds,
  formId,
  onSubmit,
  title,
  disabled,
  resetKey,
  renderOptionalColumn,
  optionalColumnWidth = 'auto',
  extraSelectButtons,
  hideSelectButtons,
  emptyState,
}: ControlPickerProps): JSX.Element => {
  const [filters, setFilters] = useState<ControlPickerFilters>(
    NO_CONTROL_PICKER_FILTERS,
  )
  const [search, setSearch] = useState('')
  useEffect(() => {
    setFilters(NO_CONTROL_PICKER_FILTERS)
    setSearch('')
  }, [resetKey])

  const allControls = useMemo(() => controls ?? [], [controls])
  const showLabels = true
  const { data: customStatuses } = useControlStatuses()
  const statusName = useCallback(
    (control: Control) =>
      getControlStatusName(
        control.getStatus(),
        control.getCustomStatusId(),
        customStatuses?.getItemsList(),
      ),
    [customStatuses],
  )

  const { options: labelOptions, isLoading: isLabelsLoading } =
    useLabelFilterOptions()
  const { data: frameworksData } = useComplianceFrameworks(true)
  const frameworkIds = useMemo(
    () => (frameworksData?.getItemsList() ?? []).map((cf) => cf.getId()),
    [frameworksData],
  )
  const { data: requirementsData } =
    useComplianceFrameworkObjectives(frameworkIds)
  const frameworkOptions = useMemo(
    () =>
      buildFrameworkFilterOptions(
        allControls,
        frameworksData?.getItemsList() ?? [],
        requirementsData?.getItemsList() ?? [],
      ),
    [allControls, frameworksData, requirementsData],
  )
  const moreOptions = useMemo(
    () => buildMoreFilterOptions(allControls, statusName),
    [allControls, statusName],
  )

  // Search matches the same fields as the controls index search.
  const searchable = useSearchableControls(allControls)
  const visibleControls = useMemo(
    () =>
      matchSearch(
        allControls.filter((control) =>
          controlMatchesPickerFilters(control, filters, statusName),
        ),
        searchable,
        search,
        (control) => control.getId(),
      ),
    [allControls, filters, statusName, search, searchable],
  )
  const visibleIds = useMemo(
    () => visibleControls.map((control) => control.getId()),
    [visibleControls],
  )
  const { selectedSet, onToggle, selectVisible, deselectVisible } =
    usePickerSelection(selectedIds, setSelectedIds, visibleIds)

  const filterDropdowns = (
    <>
      {showLabels && (
        <LocalFilterDropdown
          filterName="Labels"
          filterValues={labelOptions}
          isLoading={isLabelsLoading}
          selectedValues={filters.labelIds}
          onChangeSelectedValues={(labelIds) =>
            setFilters((previous) => ({ ...previous, labelIds }))
          }
          searchPlaceholder="Search labels..."
          noMatchesText="No matching labels found."
        />
      )}
      <LocalFilterDropdown
        filterName="Frameworks"
        filterValues={frameworkOptions}
        selectedValues={filters.frameworkIds}
        onChangeSelectedValues={(frameworkIds) =>
          setFilters((previous) => ({ ...previous, frameworkIds }))
        }
        searchPlaceholder="Search frameworks and requirements..."
        noMatchesText="No matching frameworks or requirements found."
        groupStyle="selectableAccordion"
      />
      <LocalFilterDropdown
        filterName="More filters"
        filterValues={moreOptions}
        selectedValues={filters.more}
        onChangeSelectedValues={(more) =>
          setFilters((previous) => ({ ...previous, more }))
        }
        searchPlaceholder="Search filters..."
        groupStyle="accordion"
      />
      {hasControlPickerFilters(filters) && (
        <TextButton
          onClick={(e) => {
            e.preventDefault()
            setFilters(NO_CONTROL_PICKER_FILTERS)
          }}
        >
          Clear filters
        </TextButton>
      )}
    </>
  )

  const columns = [
    ROW_COLUMNS,
    ...(renderOptionalColumn ? [optionalColumnWidth] : []),
    ...(showLabels ? ['auto'] : []),
  ].join(' ')

  return (
    <PickerFrame
      formId={formId}
      onSubmit={onSubmit}
      title={title}
      filters={filterDropdowns}
      search={search}
      setSearch={setSearch}
      noun="control"
      selectedCount={selectedIds.length}
      visibleCount={visibleControls.length}
      totalCount={allControls.length}
      isFiltered={hasControlPickerFilters(filters) || !!search.trim()}
      onSelectAll={selectVisible}
      onDeselectAll={deselectVisible}
      disabled={disabled}
      hideSelectButtons={hideSelectButtons}
      extraSelectButtons={extraSelectButtons}
      isLoading={isLoading || !controls}
      emptyState={emptyState}
    >
      <Grid gridTemplateColumns={columns}>
        {visibleControls.map((control) => (
          <ControlPickerRow
            key={control.getId()}
            control={control}
            checked={selectedSet.has(control.getId())}
            disabled={disabled}
            onToggle={onToggle}
            renderOptionalColumn={renderOptionalColumn}
            showLabels={showLabels}
          />
        ))}
      </Grid>
    </PickerFrame>
  )
}
